/**
 * applyEvent - Applies a CRM WebSocket event to the centralized store and
 * the React Query list caches.
 *
 * This is the core state mutation engine. It takes an incoming event and
 * updates state so the UI reflects changes in real time.
 *
 * Strategy:
 * - Detail/slideout: The centralized store (`crmObjectStore`) is the sole
 *   copy of the object body for these surfaces — there is no in-place cache
 *   write into React Query. `useCrmObject` and the slideout read
 *   store-first; React Query's detail/slideout entries are kept at
 *   `staleTime: Infinity` and are only ever removed (never rewritten) at
 *   explicit eviction boundaries (org switch, list type navigation — see
 *   OrgContext.tsx / crm-object-list-page.tsx).
 * - List caches: Invalidated to trigger a fast refetch. In-place cache
 *   writes are incompatible with react-aria-components' Table collection
 *   system, which uses a two-pass render architecture with a fake DOM that
 *   doesn't propagate direct cache writes to the visual table. Invalidation
 *   triggers a normal React Query refetch, which RAC's Table handles
 *   correctly.
 *
 * Seq-based deduplication (store):
 * - If event.seq <= stored object's last_event_seq -> skip (already applied)
 * - If event.seq > stored object's last_event_seq + 1 -> gap detected ->
 *   invalidate the detail/slideout/list query keys so the next mount
 *   refetches from the server and repairs the store
 * - If event.seq === stored object's last_event_seq + 1 -> apply normally
 *
 * For object_created events, there's no existing cached object, so we
 * invalidate the relevant list queries to pick up the new object.
 */

import { notifyManager, type QueryClient } from '@tanstack/react-query';

import type { PropertyValue } from '@/types/crm';

import type { EntityTypeName } from '../core/types';
import type {
  CrmObjectsListKeyParams,
  CrmObjectsListMode,
} from '../query/queries';
import { crmQueryKeys, isCrmListKey } from '../query/queries';
import {
  addPendingCreation,
  applyAssociationChange as storeApplyAssociationChange,
  applyObjectDeleted as storeApplyObjectDeleted,
  applyObjectMerged as storeApplyObjectMerged,
  applyPropertyUpdate as storeApplyPropertyUpdate,
  beginBatch,
  endBatch,
  getObjectByPk,
  resolveEntityId,
} from '../store/crmObjectStore';
import {
  allowForcedRefetch,
  beginRefetchBatch,
  endRefetchBatch,
  INTERACTIVE_REFETCH_COOLDOWN_MS,
} from './refetchThrottle';
import type { CrmWebSocketEvent } from './types';

// ---------------------------------------------------------------------------
// Debounced list invalidation
// ---------------------------------------------------------------------------
//
// WebSocket events arrive in bursts (a single property edit produces 2-3
// events: the update itself plus formula/rollup cascades). Each event was
// previously triggering immediate list query invalidation, causing a storm
// of concurrent API refetches that could exhaust DB connections.
//
// Instead, we accumulate *which* queries need invalidation into Sets and
// flush them all in one `notifyManager.batch()` after a short debounce
// window. The store write for detail/slideout state remains synchronous.
// ---------------------------------------------------------------------------

// Debounce window for batching list/aggregate invalidations triggered by WS
// events. The store is updated optimistically (synchronous) on each event,
// so list components show changes instantly via useSyncExternalStore. This
// debounced flush is the *confirmation refetch* that reconciles the optimistic
// state with the server.
//
// 150ms caused a 6-query invalidation bundle to fire ~140× per 18-hour session
// (3,600+ redundant API calls). 2 seconds batches more events per flush window
// without a perceptible UX delay — the user already sees the change from the
// store, so the server roundtrip is invisible.
const INVALIDATION_DEBOUNCE_MS = 2000;

// Association types that affect the `access_summary` field on an entity.
// When one of these fires, the source entity's detail cache and list must
// re-fetch so the access badge reflects the new privacy state.
const PRIVACY_AFFECTING_ASSOCIATION_TYPES = new Set([
  'ahoy_meeting_visible_to_org',
  'ahoy_meeting_to_user',
  'ahoy_meeting_to_integration',
  'ahoy_email_thread_visible_to_org',
  'ahoy_email_thread_to_user',
  'ahoy_email_thread_to_integration',
  'ahoy_meeting_notes_visible_to_org',
  'ahoy_meeting_notes_to_user',
  'ahoy_meeting_notes_to_integration',
  'ahoy_file_visible_to_org',
  'ahoy_file_to_user',
  'ahoy_file_to_meeting',
  'ahoy_file_to_meeting_notes',
  'ahoy_file_to_email_thread',
]);

// Note: email→entity resolution is now handled inside `crmObjectStore` via
// its `emailIndex` (maintained by upsertObject / applyPropertyUpdate /
// applyObjectMerged / applyObjectDeleted). Consumers subscribe to the index
// directly through `usePersonByEmail` → `useSyncExternalStore`, so no
// React-Query-level invalidation is needed here. The previous
// invalidatePersonByEmailQueries helper has been removed.

/** Pending invalidation sets — accumulated across events, flushed after debounce. */
let pendingListTypes = new Set<EntityTypeName>();
let pendingCrossTypes = new Set<EntityTypeName>();
let pendingRelatedIds = new Set<string>();
let pendingTimelineIds = new Set<string>();
let pendingAggregateTypes = new Set<EntityTypeName>();
let pendingObjectEvents: CrmWebSocketEvent[] = [];
let pendingQueryClient: QueryClient | null = null;
let pendingOrgId: number | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Tracks privacy-affecting association events so flushInvalidations can emit
// a single summarising console.debug per debounce window.
let pendingPrivacyEntityIds = new Set<string>();
let pendingPrivacyAssocTypes = new Set<string>();

/**
 * Schedule a flush of all pending invalidations after the debounce window.
 * If a flush is already scheduled, this is a no-op (the existing timer will
 * pick up anything added since it was set).
 *
 * If the org or queryClient changes mid-debounce (e.g., user switches orgs),
 * immediately flush pending invalidations for the previous context before
 * switching to the new context.
 */
function scheduleFlush(queryClient: QueryClient, orgId: number): void {
  if (
    (pendingQueryClient && pendingQueryClient !== queryClient) ||
    (pendingOrgId !== null && pendingOrgId !== orgId)
  ) {
    flushInvalidations();
  }
  pendingQueryClient = queryClient;
  pendingOrgId = orgId;
  if (debounceTimer !== null) {
    return;
  }
  debounceTimer = setTimeout(flushInvalidations, INVALIDATION_DEBOUNCE_MS);
}

/**
 * Flush all pending list invalidations in a single `notifyManager.batch()`.
 *
 * Exported for tests — call directly to synchronously flush without waiting
 * for the debounce timer.
 */
export function flushInvalidations(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  const qc = pendingQueryClient;
  const org = pendingOrgId;
  if (!qc || org === null) {
    return;
  }

  // Snapshot and clear before flushing — if new events arrive during
  // flush (unlikely but safe), they'll start a new debounce cycle.
  const listTypes = pendingListTypes;
  const crossTypes = pendingCrossTypes;
  const relatedIds = pendingRelatedIds;
  const timelineIds = pendingTimelineIds;
  const aggregateTypes = pendingAggregateTypes;
  const objectEvents = pendingObjectEvents;
  const privacyEntityIds = pendingPrivacyEntityIds;
  const privacyAssocTypes = pendingPrivacyAssocTypes;

  pendingListTypes = new Set();
  pendingCrossTypes = new Set();
  pendingRelatedIds = new Set();
  pendingTimelineIds = new Set();
  pendingAggregateTypes = new Set();
  pendingObjectEvents = [];
  pendingPrivacyEntityIds = new Set();
  pendingPrivacyAssocTypes = new Set();
  pendingQueryClient = null;
  pendingOrgId = null;

  // Open a forced-refetch batch so a query matched by more than one
  // invalidator in this single flush (e.g. a privacy-affecting association
  // hits both invalidateListQueries and invalidateObjectQueries) refetches
  // once now rather than also queuing a redundant ~15s catch-up.
  beginRefetchBatch();
  try {
    notifyManager.batch(() => {
      for (const type of listTypes) {
        invalidateListQueries(qc, org, type);
      }
      for (const type of crossTypes) {
        invalidateCrossTypeListQueries(qc, org, type);
      }
      for (const id of relatedIds) {
        invalidateRelatedQueries(qc, org, id);
      }
      for (const id of timelineIds) {
        invalidateTimelineQueries(qc, id);
      }
      for (const type of aggregateTypes) {
        invalidateAggregateQueries(qc, org, type);
      }
      for (const event of objectEvents) {
        invalidateObjectQueries(qc, org, event);
      }
    });
  } finally {
    endRefetchBatch();
  }

  if (privacyEntityIds.size > 0) {
    console.warn('[applyEvent] access_summary invalidation', {
      eventTypes: [...privacyAssocTypes],
      entityCount: privacyEntityIds.size,
    });
  }
}

/**
 * Options for applying events.
 */
export interface ApplyEventOptions {
  /**
   * When true, skip invalidating list queries. Used for optimistic updates
   * where the server hasn't processed the event yet — a list refetch would
   * return stale data and is wasteful. List invalidation happens naturally
   * when the real WS event arrives after the server processes the mutation.
   */
  skipListInvalidation?: boolean;
}

/**
 * Apply a single CRM event to the React Query cache.
 *
 * IMPORTANT: The event types handled here must stay in sync with:
 * 1. Backend projection handlers in apps/crm_events/services/projections/dispatcher.py
 *    (the handlers dict — determines which events mutate projected state)
 * 2. Backend broadcast whitelist in apps/crm_events/services/websocket_broadcast_callback.py
 *    (BROADCAST_EVENT_TYPES — determines which events are sent over WebSocket)
 *
 * When a new event type is added that changes CRM object data, it must be
 * handled in all three locations. Event types that don't change projected
 * data (email_sent, email_received, errored) and schema events
 * (property_type_*) are intentionally excluded.
 *
 * @param queryClient - React Query client
 * @param orgId - Current org ID
 * @param event - The CRM event to apply
 * @param options - Options controlling side effects
 */
export function applyCrmEvent(
  queryClient: QueryClient,
  orgId: number,
  event: CrmWebSocketEvent,
  options?: ApplyEventOptions
): void {
  switch (event.event_type_name) {
    case 'ahoy_property_updated':
      applyPropertyUpdated(queryClient, orgId, event, options);
      break;

    case 'ahoy_object_created':
      applyObjectCreated(queryClient, orgId, event, options);
      break;

    case 'ahoy_object_deleted':
      applyObjectDeleted(queryClient, orgId, event, options);
      break;

    case 'ahoy_association_added':
    case 'ahoy_association_removed': {
      // Apply the association change directly to the source entity in the
      // store so list cells and other store-backed UI update within one
      // frame, matching the latency of property updates.
      //
      // Previously this case deferred entirely to a debounced refetch on
      // the rationale that associations were "structural changes that
      // can't be applied incrementally". That was incorrect — adding or
      // removing one entry from `associations` is purely incremental — and
      // the refetch path lost the new associations through
      // `upsertSummaryObjects` whenever the entity was already in the
      // store as a complete entry (e.g., after a slideout open). The
      // visible regression was lifecycle-stage cells flipping to "—" or
      // staying stale after an edit.
      //
      // The cross-entity (target side) refetch below remains: when a
      // contact is linked to a deal, the deal's reverse-association list
      // needs a refetch because we don't always receive a separate WS
      // event for the target side.
      const associationTypeName = event.payload?.association_type_name as
        | string
        | undefined;
      const targetEntityId = event.payload?.target_entity_id as
        | string
        | undefined;
      if (associationTypeName && targetEntityId) {
        storeApplyAssociationChange(
          event.entity_id,
          event.event_type_name === 'ahoy_association_added'
            ? 'added'
            : 'removed',
          associationTypeName,
          targetEntityId,
          event.seq,
          // Authoritative delivery: swap in a fresh element reference even for an
          // idempotent re-add of an already-present pair. This lets an in-flight
          // optimistic rollback recognise (by reference identity) that this pair
          // was re-confirmed by the server and preserve it, instead of stripping
          // a server-confirmed association.
          { forceNewReference: true }
        );
      }

      // Associations affect both source and target objects.
      // Invalidate slideout caches directly — invalidateRelatedQueries
      // skips slideout keys, and invalidateObjectQueries only matches
      // detail/list keys. Without this, an open slideout goes stale.
      invalidateSlideoutCache(queryClient, orgId, event.entity_id);
      if (targetEntityId) {
        invalidateSlideoutCache(queryClient, orgId, targetEntityId);
      }

      // Schedule debounced invalidation for standard + related queries.
      if (!options?.skipListInvalidation) {
        scheduleFlush(queryClient, orgId);
        pendingObjectEvents.push(event);
        pendingRelatedIds.add(event.entity_id);
        // Associations alter both sides of the relationship — the source and
        // target timelines both need to refetch so the "X was linked to Y"
        // entry shows up without a page reload.
        pendingTimelineIds.add(event.entity_id);
        if (targetEntityId) {
          pendingRelatedIds.add(targetEntityId);
          pendingTimelineIds.add(targetEntityId);
        }

        // Privacy-affecting associations change access_summary on the source
        // entity. Explicitly schedule a list-type invalidation so the list
        // row refetches and re-renders the updated badge, and track the event
        // for the per-flush debug summary.
        if (
          associationTypeName &&
          PRIVACY_AFFECTING_ASSOCIATION_TYPES.has(associationTypeName)
        ) {
          pendingListTypes.add(event.entity_type_name as EntityTypeName);
          pendingPrivacyEntityIds.add(event.entity_id);
          pendingPrivacyAssocTypes.add(associationTypeName);
        }
      }
      break;
    }

    case 'ahoy_object_merged': {
      // Store-side merge handling: register the alias and transfer email
      // index entries so consumers reading `email → entity` from the store
      // re-render against the canonical entity within one tick. Properties
      // and associations on the canonical entity catch up via the debounced
      // refetch scheduled below.
      const mergedCanonicalId = event.payload?.canonical_entity_id;
      if (
        typeof mergedCanonicalId === 'string' &&
        mergedCanonicalId.length > 0
      ) {
        storeApplyObjectMerged(event.entity_id, mergedCanonicalId);
      }

      // Slideout cache uses a different key — invalidate explicitly so an
      // open slideout for the merged source picks up the canonical data.
      invalidateSlideoutCache(queryClient, orgId, event.entity_id);

      if (!options?.skipListInvalidation) {
        scheduleFlush(queryClient, orgId);
        pendingObjectEvents.push(event);
        pendingRelatedIds.add(event.entity_id);
        // Merge produces timeline entries on both the source entity and the
        // surviving (canonical) entity.
        pendingTimelineIds.add(event.entity_id);
        const canonicalEntityId = event.payload?.canonical_entity_id;
        if (
          typeof canonicalEntityId === 'string' &&
          canonicalEntityId.length > 0
        ) {
          pendingRelatedIds.add(canonicalEntityId);
          pendingTimelineIds.add(canonicalEntityId);
        } else {
          console.warn(
            '[CRM] ahoy_object_merged missing canonical_entity_id — surviving entity timeline not invalidated',
            { orgId }
          );
        }
        pendingAggregateTypes.add(event.entity_type_name as EntityTypeName);
      }
      break;
    }

    default:
      // Unknown event type — ignore
      break;
  }
}

/**
 * Apply a batch of CRM events to the cache.
 * Events are applied in order. Each event's seq check prevents double-application.
 *
 * All cache mutations are wrapped in notifyManager.batch() so that
 * React Query observer notifications are coalesced into a single flush.
 * This ensures component re-renders happen once after all events are
 * applied, rather than per-event.
 *
 * @param options - Pass `{ skipListInvalidation: true }` for optimistic updates
 *   where the server hasn't processed the event yet.
 */
export function applyCrmEventBatch(
  queryClient: QueryClient,
  orgId: number,
  events: CrmWebSocketEvent[],
  options?: ApplyEventOptions
): void {
  beginBatch();
  try {
    notifyManager.batch(() => {
      for (const event of events) {
        applyCrmEvent(queryClient, orgId, event, options);
      }
    });
  } finally {
    endBatch();
  }
}

// --- Event-specific handlers ---

function applyPropertyUpdated(
  queryClient: QueryClient,
  orgId: number,
  event: CrmWebSocketEvent,
  options?: ApplyEventOptions
): void {
  const timelineType = event.entity_type_name as EntityTypeName;
  const properties = (event.payload?.properties ?? {}) as Record<
    string,
    unknown
  >;

  // Build PropertyValue entries once for the store write.
  const propertyValues: Record<string, PropertyValue> = {};
  for (const [key, value] of Object.entries(properties)) {
    propertyValues[key] = {
      value,
      source_type_name: event.source_type_name,
      source_id: event.source_id,
    } as PropertyValue;
  }

  // Write to centralized store (seq-guarded — safe to call unconditionally).
  // The store is the sole copy of the object body for detail/slideout reads;
  // there is no direct React Query cache write for either cache.
  const storeResult = storeApplyPropertyUpdate(
    event.entity_id,
    timelineType,
    propertyValues,
    event.seq,
    orgId
  );

  // Note: ahoy_email changes are handled by `storeApplyPropertyUpdate` above,
  // which reindexes the store's emailIndex. Subscribers via
  // `subscribeToEmailIndex` re-render automatically — no separate
  // invalidation needed.

  // Store write (above) handles same-type list updates — useCrmObjectList
  // enriches query results with store data via useSyncExternalStore.
  // Same-type list invalidation is NOT needed for property updates, EXCEPT
  // when the store returns 'gap' (events were missed) — then we must invalidate
  // to refetch fresh data.
  if (!options?.skipListInvalidation) {
    if (storeResult === 'gap') {
      // Gap detected — invalidate same-type lists (this prefix also matches
      // the detail query for this type, since crmQueryKeys.objectDetail is a
      // child key of crmQueryKeys.objects) and the slideout cache directly —
      // the slideout key family ('object', singular) lives outside the
      // 'objects' prefix so it needs its own explicit invalidation.
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(orgId, timelineType),
      });
      invalidateSlideoutCache(queryClient, orgId, event.entity_id);
    }

    // Cross-type and related invalidation still needed — these handle
    // edge cases like company lists embedding contact data, or association
    // panels referencing this object. They're rare and need server data.
    scheduleFlush(queryClient, orgId);
    pendingCrossTypes.add(timelineType);
    pendingRelatedIds.add(event.entity_id);
    // Record timelines subscribe to their entity via a separate query key
    // (`['entity-view-state', entityId, ...]`) that isn't covered by list or
    // related invalidation. Without this, the timeline shows a stale view
    // after a property change until the user navigates away and back.
    pendingTimelineIds.add(event.entity_id);
  }
}

function applyObjectCreated(
  queryClient: QueryClient,
  orgId: number,
  event: CrmWebSocketEvent,
  options?: ApplyEventOptions
): void {
  const timelineType = event.entity_type_name as EntityTypeName;

  // Build partial CrmObject from event payload for optimistic list insertion.
  // Creation events include initial properties — enough for list display
  // (name, email, domain, etc.).
  const rawProperties = (event.payload?.properties ?? {}) as Record<
    string,
    unknown
  >;
  const propertyValues: Record<string, PropertyValue> = {};
  for (const [key, value] of Object.entries(rawProperties)) {
    propertyValues[key] = { value } as PropertyValue;
  }

  // Insert into the store as a partial entry. applyPropertyUpdate creates
  // the object if it doesn't exist, with isComplete: false.
  // Only add to pending creations if the event was actually applied
  // (not stale/duplicate).
  const result = storeApplyPropertyUpdate(
    event.entity_id,
    timelineType,
    propertyValues,
    event.seq,
    orgId
  );

  // Track as pending creation so the list hook can inject it immediately,
  // before the debounced list refetch completes.
  if (result === 'applied') {
    addPendingCreation(event.entity_id);
  }

  // Note: email→entity resolution updates are handled inside the store via
  // `applyPropertyUpdate` (called above) — it reindexes any ahoy_email
  // values present in the payload. No personByEmail invalidation needed
  // here; `usePersonByEmail` subscribers receive the change directly via
  // `subscribeToEmailIndex`.

  if (options?.skipListInvalidation) {
    return;
  }

  // Schedule debounced invalidation for the authoritative refetch.
  scheduleFlush(queryClient, orgId);
  pendingListTypes.add(timelineType);
  pendingCrossTypes.add(timelineType);
  pendingAggregateTypes.add(timelineType);
  pendingRelatedIds.add(event.entity_id);
}

function applyObjectDeleted(
  queryClient: QueryClient,
  orgId: number,
  event: CrmWebSocketEvent,
  options?: ApplyEventOptions
): void {
  const timelineType = event.entity_type_name as EntityTypeName;

  // Compute deletedAt once so the store's deleted_at is consistent with the event.
  const deletedAt = event.occurred_at ?? new Date().toISOString();

  // Mark deleted in centralized store (seq-guarded — safe to call unconditionally).
  // The store is the sole copy of the object body for detail/slideout reads;
  // there is no direct React Query cache write for either cache.
  storeApplyObjectDeleted(event.entity_id, event.seq, deletedAt);

  // Schedule debounced list invalidation to remove the deleted object.
  if (!options?.skipListInvalidation) {
    scheduleFlush(queryClient, orgId);
    pendingListTypes.add(timelineType);
    pendingCrossTypes.add(timelineType);
    pendingAggregateTypes.add(timelineType);
    pendingRelatedIds.add(event.entity_id);
  }
}

function invalidateObjectQueries(
  queryClient: QueryClient,
  orgId: number,
  event: CrmWebSocketEvent
): void {
  const timelineType = event.entity_type_name as EntityTypeName;

  // Predicate form so allowForcedRefetch can gate against sustained WS
  // event storms re-firing these broad matches every ~2s flush.
  //
  // Type-prefix match (not a 'list'-only match) so this covers the whole
  // objects-of-type subtree — list AND detail queries alike. Infinite-scroll
  // lists now share this same key family under the unified list-key
  // producer (crmQueryKeys.objectsListUnified), so no separate branch is
  // needed for them. Without full-subtree coverage, association events
  // (which only land here via pendingObjectEvents, not pendingListTypes)
  // would leave detail caches stuck on stale data even after the store is
  // updated.
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }
      // Structure: ['crm', orgId, 'objects', timelineType, ...]
      if (
        key[0] === 'crm' &&
        key[1] === orgId &&
        key[2] === 'objects' &&
        key[3] === timelineType
      ) {
        return allowForcedRefetch(queryClient, query.queryHash);
      }
      return false;
    },
  });
}

// --- Cache mutation helpers ---

/**
 * Invalidate non-standard queries that embed data for a specific CRM object.
 *
 * Many features cache CRM data under query keys outside the standard crmQueryKeys
 * namespace: associations, contact-context, object-for-diff, notes, research, etc.
 * Rather than maintaining a brittle allowlist of key prefixes, we match any query
 * whose key contains both the orgId and the affected timelineId.
 *
 * Standard crmQueryKeys queries (['crm', orgId, 'objects', ...]) are excluded
 * because detail reads are store-first (no direct cache writes — see the
 * module doc comment) and list is already handled precisely by
 * invalidateListQueries. Invalidating the detail cache here would trigger a
 * refetch that could regress to older server state before the event is
 * committed.
 *
 * Slideout cache keys (['crm', orgId, 'object', ...]) are also excluded
 * because the slideout is likewise store-first; explicit invalidation for it
 * is handled by invalidateSlideoutCache (association/merge events, and the
 * gap branches in applyPropertyUpdated). Invalidating here would trigger a
 * server refetch that may return stale pre-commit data.
 *
 * This is safe because React Query only refetches *observed* (mounted) queries
 * on invalidation. Unobserved queries are just marked stale and refetch lazily.
 */
function invalidateRelatedQueries(
  queryClient: QueryClient,
  orgId: number,
  timelineId: string
): void {
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }

      // Skip standard crmQueryKeys — detail is store-first (no mirroring)
      // and list is already handled precisely by invalidateListQueries.
      // Structure: ['crm', orgId, 'objects', timelineType, ...]
      if (key[0] === 'crm' && key[2] === 'objects') {
        return false;
      }

      // Skip slideout cache — also store-first; explicit invalidation is
      // handled by invalidateSlideoutCache (association/merge events, and
      // the gap branches in applyPropertyUpdated).
      // Structure: ['crm', orgId, 'object', objectId]
      if (key[0] === 'crm' && key[2] === 'object') {
        return false;
      }

      // Match any query that references both this org and this specific object.
      // Covers: associations, contact-context, object-for-diff, notes,
      // research, slideout cache, and any future query patterns.
      //
      // allowForcedRefetch (last, so it only stamps actual matches):
      // during sustained WS event streams this broad matcher re-fired for
      // the same queries on every ~2s flush — see refetchThrottle.ts.
      return (
        key.includes(orgId) &&
        key.includes(timelineId) &&
        allowForcedRefetch(queryClient, query.queryHash)
      );
    },
  });
}

/**
 * Invalidate record-timeline infinite queries for a specific entity.
 *
 * The timeline hook `useTimelineViewState` keys its query as
 * `['entity-view-state', entityId, orgId, entityType, filters]`. That prefix
 * is outside the `['crm', orgId, ...]` namespace handled by the other
 * invalidation helpers, and `invalidateRelatedQueries` doesn't catch it
 * because its key stores `orgId` as a string (from URL params) while the
 * event handler works in numeric `orgId` — the Set `key.includes(orgId)`
 * check fails on type mismatch.
 *
 * We match on entity_id alone (a globally-unique UUID), which is sufficient
 * to invalidate every open timeline looking at that entity, across any
 * filter combination, without relying on orgId type coercion.
 *
 * React Query only refetches observed queries, so unmounted timelines are
 * just marked stale and will refetch lazily the next time they mount.
 */
function invalidateTimelineQueries(
  queryClient: QueryClient,
  entityId: string
): void {
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }
      // allowForcedRefetch: refetching an infinite timeline query re-fetches
      // EVERY loaded page sequentially, so per-flush re-invalidation during
      // event streams is especially expensive — see refetchThrottle.ts.
      // INTERACTIVE cooldown (5s, not 15s): the timeline is where a user
      // watches their own edit appear — a rapid second edit must reconcile
      // quickly.
      return (
        key[0] === 'entity-view-state' &&
        key[1] === entityId &&
        allowForcedRefetch(
          queryClient,
          query.queryHash,
          INTERACTIVE_REFETCH_COOLDOWN_MS
        )
      );
    },
  });
}

/**
 * Invalidate aggregate/count queries affected by object creation or deletion.
 *
 * These queries don't reference specific timeline IDs — they contain org-level
 * aggregates (total counts, pipeline metrics) that change when the set of
 * objects changes. Only needed for create/delete/merge/restore events,
 * not property updates (which don't change counts).
 */
function invalidateAggregateQueries(
  queryClient: QueryClient,
  orgId: number,
  timelineType: EntityTypeName
): void {
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }

      // crmAggregate — all aggregate POSTs (dashboard counts, pipeline metrics,
      // column aggregations) share this key family, so one predicate covers all.
      if (key[0] === 'crmAggregate' && key[1] === orgId) {
        return allowForcedRefetch(queryClient, query.queryHash);
      }

      // crmTotalCount — per-type object count
      if (
        key[0] === 'crmTotalCount' &&
        key[1] === orgId &&
        key[2] === timelineType
      ) {
        return allowForcedRefetch(queryClient, query.queryHash);
      }

      return false;
    },
  });
}

/**
 * Invalidate list queries of OTHER CRM types that embed associated objects.
 *
 * When a CRM object changes, list queries for other types may also be stale
 * if they use include_associated_objects: true. For example, a company list
 * that embeds associated contacts will show stale contact data after a contact
 * property update.
 *
 * Both the paged and infinite-scroll list families now live under the same
 * unified key (crmQueryKeys.objectsListUnified), discriminated by `mode`.
 * Their gating stays distinct on purpose — merging them reintroduces the
 * poller-miss bug class this predicate previously fixed:
 * 1. Paged lists — only invalidated when include_associated_objects is true
 *    (same as before the key unification).
 * 2. Infinite-scroll lists — invalidated unconditionally regardless of the
 *    flag, since the infinite list no longer embeds associated_objects
 *    (badges lazy-hydrate via the store + by-entity-ids/) but association
 *    links / target ids still need to stay fresh.
 *
 * NEVER positional-index the unified key for these checks — `key.includes(true)`
 * silently stops matching once the boolean folds into the `params` object, and
 * `key[2]` is `'objects'` (not the entity type) under the unified shape, so a
 * stale `key[2] !== eventTimelineType` check becomes an always-true same-type
 * refetch storm. Read `mode`/`params` explicitly via the matcher instead.
 *
 * This is safe because React Query only refetches observed queries, and most
 * users have at most one list page open at a time.
 */
function invalidateCrossTypeListQueries(
  queryClient: QueryClient,
  orgId: number,
  eventTimelineType: EntityTypeName
): void {
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }

      // Unified crmQueryKeys list family:
      // ['crm', orgId, 'objects', entityType, 'list', mode, shape, params]
      if (isCrmListKey(key) && key[1] === orgId && key[2] === 'objects') {
        // Skip the same type — already handled by invalidateListQueries
        if (key[3] === eventTimelineType) {
          return false;
        }
        const mode = key[5] as CrmObjectsListMode | undefined;
        const params = key[7] as CrmObjectsListKeyParams | undefined;

        if (mode === 'paged') {
          // allowForcedRefetch: this matcher hits every OTHER type's
          // include_associated_objects list — during sustained cross-type
          // event streams (e.g. meeting enrichment) it re-fired for the
          // same dashboard/list queries on every ~2s flush, producing 9-10
          // identical 3-12s refetches in production. See refetchThrottle.ts.
          return (
            params?.include_associated_objects === true &&
            allowForcedRefetch(queryClient, query.queryHash)
          );
        }

        if (mode === 'infinite') {
          return allowForcedRefetch(queryClient, query.queryHash);
        }

        return false;
      }

      // Kind-based list queries:
      // ['crm', orgId, 'objects-by-kind', kind, 'list', filters, boolean]
      // Used by the calendar (entity_kind: 'event') and any future
      // cross-type aggregations. The frontend doesn't know the type→kind
      // mapping (registry-only on the backend), so any kind-based list
      // could include this type — invalidate them all and let React Query
      // refetch only the OBSERVED queries. In practice at most one or two
      // kind queries are open at a time (calendar + search bar), so the
      // extra refetch is negligible.
      // Intentionally NOT migrated to the unified list-key family — see
      // module-level notes; this key shape is separate from
      // crmQueryKeys.objectsListUnified.
      if (
        key[0] === 'crm' &&
        key[1] === orgId &&
        key[2] === 'objects-by-kind' &&
        key.includes('list')
      ) {
        return allowForcedRefetch(queryClient, query.queryHash);
      }

      return false;
    },
  });
}

/**
 * Invalidate list queries for a specific timeline type.
 * This triggers React Query to refetch the data, which RAC's Table
 * renders correctly (unlike in-place cache writes).
 *
 * Both the standard paginated list (`useCrmObjectList`) and the infinite
 * scroll list (`useCrmObjectListInfinite`, used by the contacts, deals, and
 * companies list pages) now share the same key family — produced by
 * crmQueryKeys.objectsListUnified and matched here via isCrmListKey — so a
 * single invalidation pass covers both. The infinite list no longer carries
 * an embedded `associated_objects` map — company badges on list rows
 * lazy-hydrate via the crmObjectStore and `POST /crm-objects/by-entity-ids/`
 * with loading-skeleton / partial / failed-state fallback.
 */
function invalidateListQueries(
  queryClient: QueryClient,
  orgId: number,
  timelineType: EntityTypeName
): void {
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key) || !isCrmListKey(key)) {
        return false;
      }
      return (
        key[1] === orgId &&
        key[3] === timelineType &&
        allowForcedRefetch(queryClient, query.queryHash)
      );
    },
  });
}

/**
 * Invalidate the slideout cache for a specific object, triggering a refetch.
 *
 * Used for events where we can't do an in-place update (associations, merges)
 * because we don't know what changed structurally — the full server response
 * is needed. This is safe because by the time the WS event reaches the client,
 * the server has already committed the projection update.
 *
 * The slideout query now returns a null sentinel (overlay lives in the store).
 * The skip-gate keys on request STATUS: the entry stays 'success' until this
 * function removes it, which re-enables the fetch so the overlay refreshes.
 * Because query.state.data is null, we match numeric-PK / merge-alias keys by
 * resolving them through the store rather than reading the cached body shape.
 */
function invalidateSlideoutCache(
  queryClient: QueryClient,
  orgId: number,
  timelineId: string
): void {
  // Find all slideout cache entries that match this object (by key or entity_id)
  const cache = queryClient.getQueryCache();
  const matchingQueries = cache.findAll({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }

      // Match slideout cache pattern: ['crm', orgId, 'object', objectId]
      if (key[0] !== 'crm' || key[1] !== orgId || key[2] !== 'object') {
        return false;
      }

      // Match if the key's objectId is the timelineId (UUID or numeric PK)
      if (key[3] === timelineId) {
        return true;
      }

      // The query returns null (sentinel), so cached body is not available.
      // Resolve the key's id through the store to catch numeric-PK opens and
      // merge-alias opens whose canonical entity_id matches timelineId.
      const keyId = String(key[3]);
      if (
        getObjectByPk(keyId)?.entity_id === timelineId ||
        resolveEntityId(keyId) === timelineId
      ) {
        return true;
      }

      return false;
    },
  });

  for (const query of matchingQueries) {
    // Remove (not just invalidate) so that the skip-gate sees the status entry
    // as gone and re-enables its fetch on the next open / WS-triggered render.
    queryClient.removeQueries({ queryKey: query.queryKey });
  }
}
